
/**
 * Module dependencies.
 */

import { IconButton } from './buttons/icon-button';
import { Text } from './typography';
import { color } from 'react-components/styles';
import { compact, concat, includes } from 'lodash';
import { ifProp, theme } from 'styled-tools';
import { useFieldState } from '@seegno/react-forms';
import { useTranslate } from '../i18n';
import closeIcon from './assets/svg/16/close.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  disabled?: boolean;
  name: string;
  onRemove: () => void;
  value: string;
};

/**
 * `Tags` type.
 */

type Tags = Array<string>;

/**
 * Export `addTag`.
 */

export function addTag(
  tags: Tags | null | undefined,
  tag: string,
  callback?: (tags: Tags | null | undefined) => void
): void {
  if (!includes(tags, tag)) {
    const newTags = compact(concat(tags, tag));

    if (callback) {
      callback(newTags);
    }
  }
}

/**
 * Export `removeTag`.
 */

export function removeTag(tags: Tags | null | undefined, tag: string): Tags | undefined {
  if (!tags) {
    return undefined;
  }

  return tags.length > 1 ? tags.filter(item => item !== tag) : undefined;
}

/**
 * Export `removeLastTag`.
 */

export function removeLastTag(tags: Tags | null | undefined): Tags | undefined {
  if (!tags) {
    return undefined;
  }

  return tags.length > 1 ? tags.slice(0, tags.length - 1) : undefined;
}

/**
 * `TagWrapper` styled component.
 */

const TagWrapper = styled.div<{ disabled: boolean; hasError: boolean }>`
  align-items: center;
  background-color: ${color.transparentize('primary', 0.1)};
  border-radius: 16px;
  color: ${color('primary')};
  column-gap: 8px;
  display: flex;
  justify-content: center;
  padding: 4px 16px;
  transition: ${theme('transitions.defaultTransition')};
  transition-property: background-color, color, opacity;

  ${ifProp('hasError', css`
    background-color: ${color('danger100')};
    color: ${color('danger')};
  `)}

  ${ifProp('disabled', css`
    opacity: 0.55;
  `)}
`;

/**
 * Export `Tag` component.
 */

export function Tag(props: Props): JSX.Element {
  const { className, disabled = false, name, onRemove, value } = props;
  const { translate } = useTranslate();
  const { error } = useFieldState(name);

  return (
    <TagWrapper
      className={className}
      disabled={disabled}
      hasError={!!error}
      onClick={(event: any) => {
        if (event && event.stopPropagation) {
          event.stopPropagation();
        }
      }}
    >
      <Text
        as={'span'}
        size={'normal'}
      >
        {value}
      </Text>

      <IconButton
        aria-label={translate('common.actions.delete')}
        colorTheme={'transparent'}
        disabled={disabled}
        icon={closeIcon}
        iconSize={'16px'}
        onClick={onRemove}
        size={'16px'}
        type={'button'}
      />
    </TagWrapper>
  );
}
